import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import { listarVagas, obterCidade, caminhoVaga } from "../services/api";
import { noSiteDaEmpresa } from "../utils/candidatura";
import { reputacaoEmLinha } from "../utils/reputacao";

// 🏙️ PÁGINA DE CIDADE: /vagas-em/palmas-pr → "Vagas de emprego em Palmas/PR" (03/11/2026).
//
// Nasceu do Search Console de outubro: fora as páginas de vaga, o orgânico chega por busca de cauda longa por
// cidade ("vagas de emprego palmas pr", 12 cliques) e caía no mural genérico. Esta página responde a busca
// com o nome da cidade no título e no H1.
//
// O servidor (SeoMeta.PaginaCidade) já entrega título, descrição, JSON-LD e a lista em HTML para o Google;
// aqui é a versão que a pessoa usa, paginada pelo MESMO GET /api/vagas do mural, com o filtro ?cidade= que
// ele já tem. O texto exato do filtro vem da API da cidade (o slug perde acento e hífen, e o filtro precisa
// do nome como está gravado).
const POR_PAGINA = 25;

export default function CidadeVagasPage() {
  const { cidadeSlug } = useParams();
  const [cidade, setCidade] = useState(null);
  const [semVaga, setSemVaga] = useState(false);
  const [vagas, setVagas] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  // Troca de cidade (link "Vagas em outras cidades") volta para a primeira página.
  useEffect(() => {
    let vivo = true;
    setCidade(null); setSemVaga(false); setPagina(1); setErro("");
    obterCidade(cidadeSlug)
      .then((c) => { if (vivo) setCidade(c); })
      .catch((e) => {
        if (!vivo) return;
        if (e.status === 404) { setSemVaga(true); setCarregando(false); }
        else { setErro(e.message || "Não foi possível carregar as vagas."); setCarregando(false); }
      });
    return () => { vivo = false; };
  }, [cidadeSlug]);

  useEffect(() => {
    if (!cidade) return;
    let vivo = true;
    setCarregando(true);
    listarVagas({ cidade: cidade.filtro, take: POR_PAGINA, skip: (pagina - 1) * POR_PAGINA })
      .then((r) => { if (!vivo) return; setVagas(r?.itens || []); setTotal(r?.total ?? 0); setErro(""); })
      .catch((e) => { if (vivo) setErro(e.message || "Não foi possível carregar as vagas."); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [cidade, pagina]);

  // Mesmo título que o servidor entrega, para a aba não trocar de texto quando o React assume.
  useEffect(() => {
    if (cidade) document.title = `Vagas de emprego em ${cidade.rotulo} | eaiRH`;
    else if (semVaga) document.title = "Nenhuma vaga aberta nesta cidade | eaiRH";
  }, [cidade, semVaga]);

  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const linkBusca = cidade ? `/vagas?cidade=${encodeURIComponent(cidade.filtro)}` : "/vagas";
  const irPara = (n) => { setPagina(n); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">
        <nav className="text-xs text-gray-500" aria-label="Trilha">
          <Link to="/" className="hover:underline">Início</Link>
          <span className="mx-1.5">›</span>
          <Link to="/vagas" className="hover:underline">Vagas</Link>
          {cidade && <><span className="mx-1.5">›</span><span>{cidade.rotulo}</span></>}
        </nav>

        {semVaga ? (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 text-center">
            <h1 className="text-2xl font-bold text-text">Nenhuma vaga aberta nesta cidade agora</h1>
            <p className="mt-2 text-gray-600">As vagas desta cidade foram encerradas ou o endereço não corresponde a uma cidade com vagas no eaiRH.</p>
            <Link to="/vagas" className="mt-5 inline-block rounded-xl bg-primary hover:opacity-90 text-white font-semibold px-5 py-2.5">Ver todas as vagas</Link>
          </div>
        ) : (
          <>
            <h1 className="mt-3 text-2xl md:text-3xl font-bold text-text">
              {cidade ? `Vagas de emprego em ${cidade.rotulo}` : "Vagas de emprego"}
            </h1>
            <p className="mt-2 text-gray-600">
              {cidade && !carregando
                ? <><strong>{total}</strong> {total === 1 ? "vaga aberta" : "vagas abertas"} em {cidade.rotulo}. Ao lado de cada uma, a reputação da empresa no processo seletivo.</>
                : "Carregando as vagas..."}
            </p>
            <p className="mt-1 text-sm">
              <Link to={linkBusca} className="text-primary font-semibold hover:underline">Abrir na busca completa, com todos os filtros →</Link>
            </p>

            {erro && <p className="mt-6 text-red-600">{erro}</p>}

            <ul className="mt-6 space-y-2">
              {carregando && !erro && <li className="text-gray-400 text-sm px-1">Carregando...</li>}
              {!carregando && vagas.map((v) => {
                const r = reputacaoEmLinha(v.empresaScore, v.empresaSelo, v.empresaClassificacao);
                return (
                  <li key={v.id}>
                    {/* Link de verdade (<a>), não div com onClick: é a lista que o Google segue até as vagas. */}
                    <Link to={caminhoVaga(v)} className="block rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:border-primary/40 hover:bg-gray-50">
                      <div className="font-bold text-gray-900 break-words">{v.titulo}</div>
                      <div className="text-sm text-gray-600 mt-0.5">
                        {v.empresa}{v.empresaVerificada && <span className="text-blue-600"> ✔</span>}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                        {v.senioridade && <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5">{v.senioridade}</span>}
                        {v.remoto && <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5">Remota</span>}
                        {noSiteDaEmpresa(v)
                          ? <span className="rounded-full bg-orange-50 text-orange-800 px-2 py-0.5 font-semibold">no site da empresa</span>
                          : <span className="rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 font-semibold">candidatura pelo eaiRH</span>}
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${r.cor}`} title={r.titulo}>{r.texto}</span>
                        {v.empresaDaRetorno && <span className="rounded-full bg-sky-50 text-sky-800 px-2 py-0.5 font-semibold">dá retorno</span>}
                      </div>
                    </Link>
                  </li>
                );
              })}
              {!carregando && !erro && cidade && vagas.length === 0 && (
                <li className="text-center text-gray-500 py-8">Nenhuma vaga aberta em {cidade.rotulo} agora.</li>
              )}
            </ul>

            {paginas > 1 && (
              <div className="mt-6 flex items-center justify-center gap-3 text-sm">
                <button type="button" disabled={pagina <= 1 || carregando} onClick={() => irPara(pagina - 1)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40">← Anterior</button>
                <span className="text-gray-600">Página {pagina} de {paginas}</span>
                <button type="button" disabled={pagina >= paginas || carregando} onClick={() => irPara(pagina + 1)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40">Próxima →</button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
